import React, { useState, useEffect } from 'react';

// ============================================================
//  MARKETING · CAMPAGNES
//  Branché sur GET /api/marketing/campagnes (vraies données CRM)
//  Phase 1 : leads, clients installés, CA par campagne.
//  Phase 2 (API Google Ads) : colonnes Coût et ROI.
// ============================================================

const COLORS = {
  accent: '#1FD1FD',
  accentDark: '#0E7A99',
  accentSoft: '#E6FAFF',
  glassBg: 'rgba(255,255,255,0.95)',
  glassBorder: 'rgba(227,232,238,1)',
  glassShadow: '0 1px 0 rgba(255,255,255,1) inset, 0 1px 3px rgba(50,50,93,0.06), 0 1px 2px rgba(50,50,93,0.04)',
  textPrimary: '#1A1F36',
  textSecondary: '#4A5568',
  textMuted: '#697386',
  divider: 'rgba(227,232,238,1)',
  success: '#006908',
  warning: '#BB5500',
  rowHover: 'rgba(31,209,253,0.05)'
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

const CARD_STYLE = {
  borderRadius: '16px',
  padding: '14px 16px',
  background: COLORS.glassBg,
  border: `0.5px solid ${COLORS.glassBorder}`,
  boxShadow: COLORS.glassShadow
};

const CANAL_STYLE = {
  'Google Ads': { bg: 'rgba(66,133,244,0.10)', fg: '#1A56C4' },
  'Meta Ads': { bg: 'rgba(99,102,241,0.10)', fg: '#4338CA' },
  'Autre': { bg: 'rgba(105,115,134,0.10)', fg: '#4A5568' }
};

function CanalBadge({ canal }) {
  const s = CANAL_STYLE[canal] || CANAL_STYLE['Autre'];
  return (
    <span style={{
      fontSize: '11px', fontWeight: 500, padding: '3px 9px', borderRadius: '100px',
      background: s.bg, color: s.fg, whiteSpace: 'nowrap'
    }}>
      {canal}
    </span>
  );
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div style={{ ...CARD_STYLE, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <span style={{ fontSize: '11px', letterSpacing: '0.4px', color: COLORS.textMuted, fontWeight: 500, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: '26px', fontWeight: 500, color: accent || COLORS.textPrimary, letterSpacing: '-0.4px', lineHeight: 1 }}>
        {value}
      </span>
      {sub && <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{sub}</span>}
    </div>
  );
}

const MarketingCampagnes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/marketing/campagnes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error(`Le serveur a répondu ${response.status}`);
        }
        const json = await response.json();
        if (json && json.data) {
          setData(json.data);
        } else {
          throw new Error('Réponse inattendue du serveur');
        }
      } catch (err) {
        setError(err.message || 'Impossible de charger les campagnes');
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const wrap = { padding: '20px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" };

  if (loading) {
    return (
      <div style={wrap}>
        <h1 style={{ fontSize: '20px', fontWeight: 500, color: COLORS.textPrimary, marginBottom: '14px' }}>Campagnes</h1>
        <div style={{ ...CARD_STYLE, padding: '40px', textAlign: 'center', color: COLORS.textMuted, fontSize: '14px' }}>
          Chargement des campagnes…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={wrap}>
        <h1 style={{ fontSize: '20px', fontWeight: 500, color: COLORS.textPrimary, marginBottom: '14px' }}>Campagnes</h1>
        <div style={{ ...CARD_STYLE, padding: '28px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: COLORS.warning, margin: '0 0 6px' }}>Les campagnes n'ont pas pu être chargées.</p>
          <p style={{ fontSize: '12.5px', color: COLORS.textMuted, margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const campagnes = data.campagnes || [];
  const totaux = data.totaux || {};

  return (
    <div style={wrap}>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        <KpiCard label="Campagnes" value={fmtNum(totaux.nb_campagnes)} sub="avec des leads" />
        <KpiCard label="Leads" value={fmtNum(totaux.leads)} sub="toutes campagnes" />
        <KpiCard label="Clients installés" value={fmtNum(totaux.clients)} sub="ventes conclues" />
        <KpiCard label="CA généré" value={fmtEur(totaux.ca)} sub="clients installés" accent={COLORS.success} />
      </div>

      <div style={{ ...CARD_STYLE, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
            <thead>
              <tr style={{ background: 'rgba(247,249,251,0.8)' }}>
                <Th align="left">Plateforme</Th>
                <Th align="left">Campagne</Th>
                <Th align="right">Leads</Th>
                <Th align="right">Clients</Th>
                <Th align="right">CA</Th>
                <Th align="right">Taux conv.</Th>
                <Th align="right">Coût</Th>
                <Th align="right">Coût / lead</Th>
                <Th align="right">ROI</Th>
              </tr>
            </thead>
            <tbody>
              {campagnes.map((c, i) => {
                const special = c.id_campagne === null;
                return (
                  <tr
                    key={i}
                    style={{ borderTop: `0.5px solid ${COLORS.divider}` }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.rowHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Td><CanalBadge canal={c.canal} /></Td>
                    <Td>
                      <span style={{
                        color: special ? COLORS.textMuted : COLORS.textPrimary,
                        fontStyle: special ? 'italic' : 'normal',
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                        {c.nom_google || c.nom}
                      </span>
                    </Td>
                    <Td align="right">{fmtNum(c.leads)}</Td>
                    <Td align="right">
                      <span style={{ fontWeight: c.clients > 0 ? 500 : 400, color: c.clients > 0 ? COLORS.textPrimary : COLORS.textMuted }}>
                        {fmtNum(c.clients)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span style={{ fontWeight: c.ca > 0 ? 500 : 400, color: c.ca > 0 ? COLORS.success : COLORS.textMuted }}>
                        {c.ca > 0 ? fmtEur(c.ca) : '—'}
                      </span>
                    </Td>
                    <Td align="right">
                      <span style={{ color: c.taux_conversion > 0 ? COLORS.textSecondary : COLORS.textMuted }}>
                        {c.taux_conversion > 0 ? `${c.taux_conversion.toLocaleString('fr-FR')} %` : '—'}
                      </span>
                    </Td>
                    <Td align="right">
                      <span style={{ color: c.cout ? COLORS.textPrimary : COLORS.textMuted }}>
                        {c.cout ? fmtEur(c.cout) : '—'}
                      </span>
                    </Td>
                    <Td align="right">
                      <span style={{ color: c.cout_par_lead ? COLORS.textSecondary : COLORS.textMuted }}>
                        {c.cout_par_lead ? `${c.cout_par_lead.toLocaleString('fr-FR')} €` : '—'}
                      </span>
                    </Td>
                    <Td align="right">
                      <span style={{ fontWeight: c.roi != null ? 500 : 400, color: c.roi == null ? COLORS.textMuted : (c.roi >= 0 ? COLORS.success : '#DC2626') }}>
                        {c.roi != null ? `${c.roi.toLocaleString('fr-FR')} %` : '—'}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `1.5px solid ${COLORS.divider}`, background: 'rgba(247,249,251,0.8)' }}>
                <Td><span style={{ fontWeight: 500, color: COLORS.textPrimary }}>Total</span></Td>
                <Td />
                <Td align="right"><strong>{fmtNum(totaux.leads)}</strong></Td>
                <Td align="right"><strong>{fmtNum(totaux.clients)}</strong></Td>
                <Td align="right"><strong style={{ color: COLORS.success }}>{fmtEur(totaux.ca)}</strong></Td>
                <Td align="right"><span style={{ color: COLORS.textMuted }}>—</span></Td>
                <Td align="right">
                  <strong style={{ color: totaux.cout ? COLORS.textPrimary : COLORS.textMuted }}>
                    {totaux.cout ? fmtEur(totaux.cout) : '—'}
                  </strong>
                </Td>
                <Td align="right"><span style={{ color: COLORS.textMuted }}>—</span></Td>
                <Td align="right">
                  <strong style={{ color: totaux.roi == null ? COLORS.textMuted : (totaux.roi >= 0 ? COLORS.success : '#DC2626') }}>
                    {totaux.roi != null ? `${totaux.roi.toLocaleString('fr-FR')} %` : '—'}
                  </strong>
                </Td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

function Th({ children, align, muted }) {
  return (
    <th style={{
      textAlign: align || 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 500,
      letterSpacing: '0.3px', textTransform: 'uppercase',
      color: muted ? 'rgba(105,115,134,0.6)' : COLORS.textMuted, whiteSpace: 'nowrap'
    }}>
      {children}
    </th>
  );
}

function Td({ children, align }) {
  return (
    <td style={{
      textAlign: align || 'left', padding: '11px 14px',
      color: COLORS.textPrimary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'
    }}>
      {children}
    </td>
  );
}

export default MarketingCampagnes;
